<script lang="ts">
	import { isAfter, isBefore, isValid, parse } from "date-fns";
	import { Input, type TimepickerProps } from "flowbite-svelte-v1";
	import Icon from "#lib/components/Icon.svelte";

	let { id = "time", value = $bindable(), endValue = $bindable(), min = "", max = "", required = true, disabled = false, inputColor, size = "md", onselect }: TimepickerProps = $props();

	// Helper functions using date-fns
	function parseTime(time: string): Date | null {
		if (!time) return null;
		const parsed = parse(time, "HH:mm", new Date());
		return isValid(parsed) ? parsed : null;
	}

	function timeToMinutes(time: string | undefined): number {
		if (!time) {
			return 0;
		}
		const date = parseTime(time);
		return date ? date.getHours() * 60 + date.getMinutes() : 0;
	}

	function isValidTimeFormat(time: string | undefined): boolean {
		if (!time) {
			return false;
		}
		return parseTime(time) !== null;
	}

	function isTimeInRange(time: string, minTime: string, maxTime: string): boolean {
		const timeDate = parseTime(time);
		if (!timeDate) return false;

		if (minTime) {
			const minDate = parseTime(minTime);
			if (minDate && isBefore(timeDate, minDate)) return false;
		}

		if (maxTime) {
			const maxDate = parseTime(maxTime);
			if (maxDate && isAfter(timeDate, maxDate)) return false;
		}

		return true;
	}

	function handleTimeChange(event: Event, isEndTime: boolean = false): void {
		const target = event.target as HTMLInputElement;
		const newValue = target.value;

		// Validate time format
		if (!isValidTimeFormat(newValue)) {
			target.value = (isEndTime ? endValue : value) ?? "";
			return;
		}

		// Validate against min/max constraints
		if (!isTimeInRange(newValue, min, max)) {
			target.value = (isEndTime ? endValue : value) ?? "";
			return;
		}

		// Use date-fns for reliable time comparison
		const newValueMinutes = timeToMinutes(newValue);
		const valueMinutes = timeToMinutes(value);
		const endValueMinutes = timeToMinutes(endValue);

		if (isEndTime) {
			if (newValueMinutes < valueMinutes) {
				// Only update start time if it respects min/max constraints
				if (isTimeInRange(newValue, min, max)) {
					value = newValue;
				} else {
					target.value = endValue ?? "";
					return;
				}
			} else {
				endValue = newValue;
			}
		} else {
			if (newValueMinutes > endValueMinutes) {
				// Only update end time if it respects min/max constraints
				if (isTimeInRange(newValue, min, max)) {
					endValue = newValue;
				} else {
					target.value = value ?? "";
					return;
				}
			} else {
				value = newValue;
			}
		}

		notifyChange();
	}

	function notifyChange(): void {
		if (onselect) {
			onselect({
				time: value ?? "",
				endTime: endValue ?? "",
			});
		}
	}

	// Ensure initial values are valid
	$effect(() => {
		if (!isValidTimeFormat(value)) {
			value = undefined;
		}
		if (!isValidTimeFormat(endValue)) {
			endValue = undefined;
		}
	});
</script>

<div class="relative inline-flex">
	<Input {id} color={inputColor} type="time" {size} {min} {max} {required} {disabled} class="relative block px-2 pr-8 focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 rtl:text-right" bind:value oninput={e => handleTimeChange(e)} onchange={e => handleTimeChange(e)} />
	<div class="pointer-events-none absolute inset-y-0 inset-e-0 top-0 flex items-center pe-3.5">
		<Icon iconSet="heroicons" name="clock" class="absolute right-2 h-5 w-5 text-gray-500 dark:text-gray-400" />
	</div>
</div>
